import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { toast } from 'sonner';
import {
  openCrmStudioInNewTab,
  shouldShowCrmStudioForCase,
} from '../../../config/crmStudio';
import CaseWorkflowProgress from '../../../components/cases/CaseWorkflowProgress';

const CaseHeader = ({
  caseData,
  onBack,
  files,
  onEdit,
  userRole,
  userRoleTypes = [],
  crmStudioSettings = null,
  phases = null,
  templateActionsByTemplateId = {},
  deliveredCase,
  showNewConceptionVersion = false,
  onCommanderSuite,
  onRaffinement,
  canCancelCase = false,
  onCancelCase,
  cancelCaseLoading = false,
  canDeleteCase = false,
  onDeleteCase,
  deleteCaseLoading = false,
  workflowProgress = null,
  parentCaseLink = null,
  childCaseLink = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      on_hold: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      archived: 'bg-muted text-muted-foreground dark:bg-muted dark:text-muted-foreground',
    };
    return colors?.[status] || colors?.new;
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      urgente: 'bg-red-500 text-white',
      urgent: 'bg-red-500 text-white',
      express: 'bg-orange-500 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-amber-500 text-white',
      normal: 'bg-blue-500 text-white',
      low: 'bg-gray-500 text-white',
    };
    return colors?.[urgency] || colors?.normal;
  };

  const handleViewStudio = () => {
    openCrmStudioInNewTab({
      patientId: caseData?.patientIddb,
      caseId: caseData?.id,
      settings: crmStudioSettings,
      files,
      toastFn: toast,
    });
  };

  const showStudioButton = shouldShowCrmStudioForCase({
    settings: crmStudioSettings,
    userRoleTypes: userRoleTypes?.length ? userRoleTypes : userRole ? [userRole] : [],
    currentPhase: caseData?.currentPhase,
    phases,
    templateActionsByTemplateId,
  });

  const PHASE_FR = {
    submission: 'Soumission de la Conception',
    review: 'Révision Initiale',
    design: 'Phase de Conception',
    production: 'Production',
    quality_check: 'Contrôle Qualité',
    shipping: 'Expédition',
    delivered: 'Livré',
  };

  const isArchived = caseData?.status === 'archived';
  const isCancelled = caseData?.status === 'cancelled';
  const versionNumber = caseData?.versionNumber ?? 1;

  return (
    <div className="case-detail-case-header bg-card border-b border-border p-6">
      {isArchived ? (
        <div className="mb-4 rounded-lg border border-border bg-muted/50 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">{t('caseVersion.archivedBanner')}</p>
          {childCaseLink ? (
            <button
              type="button"
              onClick={() => navigate(`/case-detail/${childCaseLink.id}`)}
              className="text-sm font-medium text-primary hover:underline"
            >
              {t('caseVersion.viewChild', { version: childCaseLink.versionNumber })}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack} iconName="ArrowLeft" />
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h1 className="font-semibold text-foreground">
                Conception n°{caseData?.caseNumber}
              </h1>
              {versionNumber > 1 || parentCaseLink ? (
                <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                  {t('caseVersion.badge', { version: versionNumber })}
                </span>
              ) : null}
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(caseData?.status)}`}
              >
                {caseData?.statusLabel}
              </span>
              {caseData?.urgency !== 'normal' && (
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${getUrgencyColor(caseData?.urgency)}`}
                >
                  {caseData?.urgencyLabel}
                </span>
              )}
              {caseData?.hasIncidents && (
                <span className="notification-badge urgent">
                  <Icon name="AlertCircle" size={14} />
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Patient: {caseData?.patientName} • Créé: {caseData?.createdDate}
            </p>
            {parentCaseLink ? (
              <button
                type="button"
                onClick={() => navigate(`/case-detail/${parentCaseLink.id}`)}
                className="text-xs text-primary hover:underline mt-1"
              >
                {t('caseVersion.viewParent', { version: parentCaseLink.versionNumber })}
              </button>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <>
            {canDeleteCase && typeof onDeleteCase === 'function' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={deleteCaseLoading}
                disabled={deleteCaseLoading}
                iconName="Trash2"
                iconPosition="left"
                className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={onDeleteCase}
              >
                Supprimer la conception
              </Button>
            )}

            {showStudioButton && !isCancelled && (
              <Button
                variant="default"
                iconName="ExternalLink"
                iconPosition="left"
                onClick={handleViewStudio}
                className="
            relative inline-flex items-center gap-2 px-5 py-2.5
            bg-gradient-to-r from-violet-600 to-indigo-600
            hover:from-violet-500 hover:to-indigo-500
            text-white font-semibold text-sm rounded-xl
            shadow-lg shadow-violet-500/30
            hover:shadow-violet-500/50
            transition-all duration-300 ease-out
            hover:scale-[1.03] active:scale-[0.97]
          "
              >
                Ouvrir Studio 3D
              </Button>
            )}

            {canCancelCase && typeof onCancelCase === 'function' ? (
              <details className="relative">
                <summary
                  className="flex h-11 w-11 cursor-pointer list-none items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground [&::-webkit-details-marker]:hidden"
                  aria-label="Plus d’actions"
                  title="Plus d’actions"
                >
                  <Icon name="MoreVertical" size={20} />
                </summary>
                <div className="absolute right-0 top-full z-30 mt-2 min-w-56 rounded-lg border border-border bg-card p-1.5 shadow-xl">
                  <button
                    type="button"
                    disabled={cancelCaseLoading}
                    onClick={(event) => {
                      event.currentTarget.closest('details')?.removeAttribute('open');
                      onCancelCase();
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Icon name="Ban" size={16} />
                    Annuler la commande
                  </button>
                </div>
              </details>
            ) : null}
          </>
        </div>
      </div>

      {userRole === 'dentist_client' && workflowProgress ? (
        <div className="mb-4">
          <CaseWorkflowProgress
            variant="header"
            percent={workflowProgress.percent}
            macroLabel={workflowProgress.macroLabel}
            activeGroupNum={workflowProgress.activeGroupNum}
            totalLots={workflowProgress.totalLots}
            lots={workflowProgress.lots}
          />
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        {(userRole === 'administrator' || userRole === 'dentist_client') &&
          deliveredCase &&
          caseData.status !== 'draft' &&
          !isArchived && (
            <button
              onClick={onCommanderSuite}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                borderRadius: '10px',
                border: 'none',
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #00c851, #007e33)',
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                boxShadow: '0 4px 15px rgba(0,200,81,0.35)',
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.04)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,200,81,0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,200,81,0.35)';
              }}
            >
              <Icon name="RefreshCw" size={15} />
              Commander la suite
            </button>
          )}

        {(userRole === 'administrator' || userRole === 'dentist_client') &&
          showNewConceptionVersion &&
          !isArchived && (
            <button
              onClick={onRaffinement}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                borderRadius: '10px',
                border: 'none',
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #ff4757, #c0392b)',
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                boxShadow: '0 4px 15px rgba(255,71,87,0.35)',
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.04)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(255,71,87,0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(255,71,87,0.35)';
              }}
            >
              <Icon name="Settings2" size={15} />
              Nouvelle conception 3D
            </button>
          )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="flex items-center gap-3 p-3 rounded-lg case-detail-section-surface">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="User" size={20} className="text-primary" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Dentiste</p>
            <p className="text-sm font-medium text-foreground">{caseData?.dentistName}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 rounded-lg case-detail-section-surface">
          <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center">
            <Icon name="Users" size={20} className="text-sky-600" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Prothétiste</p>
            <p className="text-sm font-medium text-foreground">{caseData?.prosthetist}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 rounded-lg case-detail-section-surface">
          <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
            <Icon name="Clock" size={20} className="text-warning" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Phase Actuelle</p>
            <p className="text-sm font-medium text-foreground">
              {PHASE_FR[caseData?.currentPhase] ?? caseData?.currentPhase ?? '—'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 rounded-lg case-detail-section-surface">
          <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center">
            <Icon name="Calendar" size={20} className="text-sky-600" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Package</p>
            <p className="text-sm font-medium text-foreground">{caseData?.packages}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseHeader;
